import { useEffect, useRef, useState } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getApiErrorMessage } from '@/lib/apiError';
import type { ExportFilters } from '@/models/entities/ExportModel';
import { useExportStore } from '@/stores/useExportStore';
import { useReferenceOptions } from '@/stores/useReferenceOptions';
import { createExport, downloadExport } from './exports';

/**
 * Bouton « Exporter » d'une liste de feuilles (Historique, Dirigeant) : un type au choix (CSV sans ou avec détails, ZIP
 * des PDF), sur les filtres de la liste. L'export se génère côté API ; il se télécharge tout seul s'il se termine pendant
 * que la page est ouverte, et reste dans les Téléchargements (icône de l'en-tête) sinon.
 */
export function ExportButton({ companyId, filters }: { companyId: number; filters: ExportFilters }) {
  const types = useReferenceOptions('exportTypes');
  const [creating, setCreating] = useState(false);
  const exports = useExportStore((state) => state.exports);
  // Exports lancés d'ici, téléchargés à la fin s'ils se terminent pendant que la page est ouverte
  const waiting = useRef(new Set<string>());

  useEffect(() => {
    for (const model of exports) {
      const id = String(model.id);
      if (!waiting.current.has(id) || model.isRunning) continue;
      waiting.current.delete(id);
      if (model.isCompleted) {
        downloadExport(id)
          .then(() => toast.success('Export prêt : téléchargement lancé.'))
          .catch((error) =>
            toast.error(getApiErrorMessage(error, "Le fichier n'a pas pu être téléchargé, veuillez réessayer.")),
          );
      } else {
        toast.error("L'export a échoué. Vous pouvez le relancer depuis les téléchargements.");
      }
    }
  }, [exports]);

  const run = async (typeReference: string) => {
    setCreating(true);
    try {
      const model = await createExport(companyId, typeReference, filters);
      waiting.current.add(String(model.id));
      toast.info('Export en cours…', {
        description:
          'Il se télécharge dès qu’il est prêt ; vous le retrouvez aussi dans les téléchargements, en haut à droite.',
      });
      await useExportStore.getState().refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "L'export n'a pas pu être lancé, veuillez réessayer."));
    } finally {
      setCreating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={creating || !types.length}>
          {creating ? <Loader2 className="animate-spin" /> : <Download />}
          Exporter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {/* Dans l'ordre de l'API (sans détails, avec détails, ZIP), pas par libellé */}
        {[...types]
          .sort((a, b) => a.id - b.id)
          .map((type) => (
            <DropdownMenuItem key={type.reference} onSelect={() => void run(type.reference)}>
              {type.label}
            </DropdownMenuItem>
          ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
